/**
 * The college (and high-school) prospect bank, and how it gets updated.
 *
 * The NFL bank has a live feed behind it. This one cannot: draft boards are journalism, they sit
 * behind Cloudflare, and none of them serves a CORS-readable JSON endpoint a browser could poll.
 * Pretending otherwise would give the league a refresh button that quietly fails in September.
 *
 * So the refresh is a paste. Every board on the internet renders as lines of "rank, name,
 * position, school" in some order with some delimiter, and copying a page out of one takes about
 * as long as clicking a button would. The parser is deliberately forgiving about which delimiter
 * and whether there is a rank; it is deliberately strict about what counts as a position, because
 * a mis-parsed line that silently becomes a player named "WR" is worse than a rejected one.
 *
 * A paste MERGES. It updates a player who is already there and adds the ones who are not, rather
 * than replacing the bank — most boards are position-specific or top-50, and a replace would
 * quietly delete everybody the pasted page did not happen to cover.
 */

import bundled from '../data/prospects-2027.json';
import { matcher } from './names.js';

export interface Prospect {
  /** Name. */
  n: string;
  /** Position. */
  p: string;
  /** School — the college, or for a high-schooler the one he is committed to. */
  s: string;
  /**
   * FantasyPros devy rank, and the one the list leads on (Tim, 9 September: "use the devy 300 as
   * my top source then").
   *
   * Its own field rather than folded into `overall` because the two measure different things: a
   * devy rank asks who is the best college asset to own, a draft-board rank asks where he goes in
   * ONE draft. Averaging them would produce a number that means neither, and the three boards
   * below cannot see an underclassman at all — which is how a man already on a farm roster came
   * to be missing from the list he was drafted from.
   */
  devy?: number;
  overall?: number;
  posRank?: number;
  consensus?: number;
  /**
   * Class-of-2027 high schooler rather than a college player. They are eligible for the farm
   * rounds like anybody else; the flag exists so the list can say where he is playing now.
   */
  hs?: boolean;
  hsRank?: number;
  hsSchool?: string;
  classYear?: number;
  /** Set on anything added or changed after the bundled snapshot. */
  addedBy?: string;
  addedAt?: string;
}

export interface ProspectBank {
  draftClass: number;
  /** ISO date of the snapshot, or of the last edit. */
  readOn: string;
  sources: string[];
  note?: string;
  prospects: Prospect[];
}

const RAW = bundled as unknown as ProspectBank;

export const BUNDLED_PROSPECTS: ProspectBank = {
  draftClass: RAW.draftClass,
  readOn: RAW.readOn,
  sources: RAW.sources,
  note: RAW.note,
  prospects: RAW.prospects,
};

/**
 * Positions a board might print. Anything else on a line means the line is not a player row —
 * a header, a page number, an ad — and it is skipped rather than guessed at.
 */
export const POSITION_ALIASES: Readonly<Record<string, string>> = {
  QB: 'QB', RB: 'RB', HB: 'RB', FB: 'RB', WR: 'WR', TE: 'TE', ATH: 'ATH',
  OT: 'OT', T: 'OT', OL: 'OG', IOL: 'OG', OG: 'OG', G: 'OG', C: 'C',
  DL: 'DL', DT: 'DT', DE: 'DE', EDGE: 'DE', ED: 'DE',
  LB: 'LB', ILB: 'LB', OLB: 'LB', CB: 'CB', S: 'S', DB: 'CB',
  K: 'K', P: 'P', LS: 'LS',
};

export const SKILL_POSITIONS = ['QB', 'RB', 'WR', 'TE'] as const;

export const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');

export interface ParseResult {
  parsed: Prospect[];
  /** Lines that did not look like a player row, with the reason. Shown, never swallowed. */
  skipped: Array<{ line: string; why: string }>;
}

/**
 * Parse a pasted board. Accepts pipe, tab, or comma separation, in any of the orders these sites
 * actually use, with an optional leading rank.
 *
 *   1|Jeremiah Smith|WR|Ohio State
 *   Jeremiah Smith, WR, Ohio State
 *   12  Arch Manning  QB  Texas
 */
export function parseProspects(text: string): ParseResult {
  const parsed: Prospect[] = [];
  const skipped: Array<{ line: string; why: string }> = [];
  const seen = new Set<string>();

  for (const raw of text.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line) continue;

    /*
     * Pipes, tabs, commas, and the dashes people paste from a group chat (Pass 16 rev B). A dash
     * only splits when it has space on both sides, so "Amon-Ra" and "Smith-Njigba" stay whole.
     */
    const parts = line
      .split(/\s*[|\t]\s*|\s*,\s*|\s+[—–-]\s+/)
      .map((x) => x.trim())
      .filter(Boolean);

    if (parts.length < 2) {
      skipped.push({ line, why: 'no position; use rank|Name|POS|School' });
      continue;
    }

    // A leading pure number (or "12." / "#12" / "rank 12") is a rank, not a name.
    let rank: number | undefined;
    const lead = /^(?:rank\s+)?#?(\d+)\.?$/i.exec(parts[0]!);
    if (lead) {
      rank = Number(lead[1]);
      parts.shift();
    }

    const posIndex = parts.findIndex((x) => POSITION_ALIASES[x.toUpperCase().replace(/[^A-Z]/g, '')]);
    if (posIndex < 0) {
      skipped.push({ line, why: 'no position; use rank|Name|POS|School' });
      continue;
    }
    const pos = POSITION_ALIASES[parts[posIndex]!.toUpperCase().replace(/[^A-Z]/g, '')]!;
    const rest = parts.filter((_, i) => i !== posIndex);
    const name = rest[0] ?? '';
    const school = rest.slice(1).join(', ');

    if (!name || name.length < 3 || !/[a-z]/i.test(name)) {
      skipped.push({ line, why: 'no name; use rank|Name|POS|School' });
      continue;
    }
    const key = norm(name);
    if (seen.has(key)) {
      skipped.push({ line, why: 'duplicate of an earlier line in this paste' });
      continue;
    }
    seen.add(key);
    parsed.push({ n: name, p: pos, s: school, ...(rank ? { overall: rank } : {}) });
  }

  return { parsed, skipped };
}

export interface MergeResult {
  bank: ProspectBank;
  added: number;
  updated: number;
}

/**
 * Fold a parsed paste into the bank. Existing players keep their identity and gain whatever the
 * paste knows; new ones are appended. High-school rows are keyed separately from college rows so
 * a name shared across a three-year gap does not collapse two different people.
 */
export function mergeProspects(
  bank: ProspectBank,
  incoming: readonly Prospect[],
  by: string,
  today = new Date().toISOString().slice(0, 10),
): MergeResult {
  const out = bank.prospects.map((p) => ({ ...p }));
  const index = new Map(out.map((p, i) => [`${p.hs ? 'hs:' : ''}${norm(p.n)}`, i]));
  let added = 0;
  let updated = 0;

  for (const p of incoming) {
    const key = `${p.hs ? 'hs:' : ''}${norm(p.n)}`;
    const at = index.get(key);
    if (at === undefined) {
      out.push({ ...p, addedBy: by, addedAt: today });
      index.set(key, out.length - 1);
      added += 1;
    } else {
      const cur = out[at]!;
      out[at] = {
        ...cur,
        p: p.p || cur.p,
        s: p.s || cur.s,
        devy: p.devy ?? cur.devy,
        overall: p.overall ?? cur.overall,
        posRank: p.posRank ?? cur.posRank,
        addedBy: by,
        addedAt: today,
      };
      updated += 1;
    }
  }

  /* Devy leads, then the draft boards, then the high schoolers — the same order the file is in. */
  out.sort(
    (a, b) => Number(Boolean(a.hs)) - Number(Boolean(b.hs))
      || (a.devy ?? 9999) - (b.devy ?? 9999)
      || (a.overall ?? 9999) - (b.overall ?? 9999)
      || (a.hsRank ?? 9999) - (b.hsRank ?? 9999)
      || a.n.localeCompare(b.n),
  );

  return {
    bank: {
      ...bank,
      readOn: today,
      sources: [...new Set([...bank.sources, `Pasted by ${by} on ${today}`])],
      prospects: out,
    },
    added,
    updated,
  };
}

/** Search across name, school and position. */
export function searchProspects(
  bank: ProspectBank,
  query: string,
  opts: { positions?: readonly string[]; includeHs?: boolean; taken?: Iterable<string> } = {},
): Prospect[] {
  const positions = opts.positions?.length ? new Set(opts.positions) : null;
  const taken = new Set([...(opts.taken ?? [])].map(norm));
  const match = matcher(query);
  const empty = !query.trim();
  return bank.prospects.filter((p) => {
    if (p.hs && opts.includeHs === false) return false;
    if (positions && !positions.has(p.p)) return false;
    if (taken.has(norm(p.n))) return false;
    if (empty) return true;
    return match(p.n) || match(p.s) || norm(p.p) === norm(query);
  });
}

/* -- the Rivals300, through the server ------------------------------------------------------ */

/**
 * The 2027 recruiting board, pulled rather than typed in (Tim, 8 September).
 *
 * THE FETCH IS A SERVER'S JOB. on3.com send no `Access-Control-Allow-Origin`, so a browser cannot
 * read the page at all — `functions/src/rivals.ts` does the reading and the parsing, and this is
 * the two lines that ask it to. It returns ROWS: the merge happens here, through `mergeProspects`
 * like the paste box, so the board's own provenance and its add-and-never-delete rule are in one
 * place rather than two.
 *
 * `auth` is passed in rather than imported, because `store/session` is a React module and this
 * file is domain — and because a test wants to hand it a fixed string.
 */
export async function refreshFromRivals(
  auth: () => Promise<string | null> = async () => null,
  fetchImpl: typeof fetch = fetch,
): Promise<Prospect[]> {
  const token = await auth();
  if (!token) throw new Error('Sign in to read the Rivals board.');
  const res = await fetchImpl('/api/prospects', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'content-type': 'application/json' },
    body: '{}',
  });
  const body = await res.json() as { error?: string; prospects?: Prospect[] };
  if (!res.ok) throw new Error(body.error ?? `The server returned ${res.status}`);
  const rows = body.prospects ?? [];
  if (rows.length === 0) throw new Error('The Rivals board came back empty.');
  return rows;
}
